/**
 * Typed kernel errors — the fail-loud contract, mechanized (ADR-001 §7).
 * Depends on: nothing. Depended on by: every kernel module and every consumer
 * that catches kernel failures by type instead of by string-matching messages.
 *
 * Rule: the kernel never returns fake-success and never silently degrades.
 * Undesigned paths throw NotDesignedYetError — a stub that announces itself.
 */

export class KernelError extends Error {
  constructor(message: string) {
    super(message);
    this.name = new.target.name;
  }
}

/** A required bone has no mapping to the ontology for the requested operation. */
export class UnmappedBoneError extends KernelError {
  constructor(
    readonly boneId: string,
    readonly operation: string,
  ) {
    super(`Required bone "${boneId}" is unmapped for operation "${operation}".`);
  }
}

/**
 * Left/right bind frames are related by REFLECTION, not rotation — the one
 * admitted open problem (ADR-005 §7). Rejected loudly until reflection-aware
 * conjugation is designed; never silently mangled the way v2 mangles it.
 */
export class ReflectedBindFrameError extends KernelError {
  constructor(readonly mirrorPair: readonly [string, string]) {
    super(
      `Bind frames of mirror pair ${mirrorPair[0]} ⇄ ${mirrorPair[1]} are reflection-related; ` +
        `reflection-aware conjugation is not yet designed (ADR-005 §7). Assisted path required.`,
    );
  }
}

/** Non-humanoid topology — a designed rejection, not a fuzzy-match attempt (ADR-003 §7). */
export class UnsupportedTopologyError extends KernelError {
  constructor(readonly detail: string) {
    super(`Unsupported skeleton topology: ${detail} (quadruped/creature ontology is deferred).`);
  }
}

/**
 * The ontology data itself is internally inconsistent — a bug in OUR data,
 * not the caller's. Carries every problem found, not just the first.
 */
export class OntologyIntegrityError extends KernelError {
  constructor(readonly problems: readonly string[]) {
    super(
      `Ontology failed ${problems.length} integrity check(s):\n` +
        problems.map((p) => `  - ${p}`).join('\n'),
    );
  }
}

/** Malformed IR data rejected at the boundary with path-precise reasons (ADR-003 §7). */
export class SchemaValidationError extends KernelError {
  constructor(
    readonly subject: string,
    readonly issues: readonly string[],
  ) {
    super(
      `${subject} failed schema validation (${issues.length} issue(s)):\n` +
        issues.map((i) => `  - ${i}`).join('\n'),
    );
  }
}

/**
 * A command the log refuses — unknown verb, or an identity the Document does
 * not hold (ADR-007 §8). Every message names what exists or what would
 * release the refusal; a refusal is atomic (the input Document is untouched).
 */
export class CommandRefusal extends KernelError {
  constructor(message: string) {
    super(message);
  }
}

/** The stub-loud rule (README meta-rule 4) as a callable. */
export class NotDesignedYetError extends KernelError {
  constructor(readonly path: string) {
    super(`NOT DESIGNED YET: "${path}" is a declared gap, not an implementation.`);
  }
}

/** Placeholder for undesigned paths: always throws, never masquerades. */
export function notDesignedYet(path: string): never {
  throw new NotDesignedYetError(path);
}
